import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

/**
 * How much of a session's cognitive trace one screen-width shows
 * (TRACE-VIEWPORT).
 *
 * The trace was drawn across whatever width the card had, whatever the
 * session's length. At the native one-point-per-second resolution a 4h35m
 * case is ~16,500 points in ~1000 px — sixteen points per pixel, which is not
 * a line anyone can read. Now the plot is drawn at a fixed seconds-per-pixel
 * and the surplus scrolls horizontally.
 *
 * This is deliberately NOT the same knob as `trace_bin_seconds`: that changes
 * the DATA (smoothing, recomputed server-side, admin-only, persisted); this
 * changes the VIEW (per-viewer, instant, nothing recomputed).
 */

/** Whole-session — the pre-scrolling behaviour, kept as the zoomed-out end. */
export const SPAN_WHOLE = 0;

export interface TraceSpan {
  seconds: number;
  label: string;
}

/**
 * 10 minutes by default: ~1.7 px per second of data at a typical card width,
 * which keeps individual attention dips distinguishable while a 90-minute
 * case is still only nine screens to pan through. 5 min reads better but
 * turns a long case into 55 screens; 30 min is close to today's unreadable
 * density.
 */
export const DEFAULT_SPAN_S = 600;

export const TRACE_SPANS: TraceSpan[] = [
  { seconds: 120, label: '2 min' },
  { seconds: 600, label: '10 min' },
  { seconds: 3600, label: '1 hr' },
  { seconds: SPAN_WHOLE, label: 'Whole' },
];

/**
 * Width the plot needs so `spanS` seconds occupy one container width.
 *
 * Never narrower than the container: a two-minute recording at a ten-minute
 * span must fill the card as it always did, not sit in a fifth of it with
 * dead space and a pointless scrollbar.
 */
export function chartWidthFor(
  durationS: number,
  spanS: number,
  containerWidth: number,
): number {
  if (containerWidth <= 0) return 0;
  if (spanS === SPAN_WHOLE || durationS <= 0) return containerWidth;
  return Math.max(containerWidth, (durationS / spanS) * containerWidth);
}

/** Whether a span is worth offering for a session of this length. */
export function spanIsUseful(span: TraceSpan, durationS: number): boolean {
  // A span longer than the session shows the same thing as 'Whole', so
  // offering it is a button that does nothing.
  return span.seconds === SPAN_WHOLE || span.seconds < durationS;
}

/** Measures a container and tracks the chosen span. */
export function useTraceViewport(durationS: number) {
  /** The scroller, for callers that need to read or set scrollLeft. */
  const el = useRef<HTMLDivElement | null>(null);
  // Held in STATE as well, because the measurement has to run when the node
  // appears — not when this hook first runs. The chart renders only once the
  // trace has loaded, so a mount-time `useEffect` with a ref sees null, bails,
  // and never runs again: the width stays 0, the chart falls back to 100%, and
  // the span buttons change a number nothing downstream uses. That shipped in
  // v0.17.0 and looked exactly like "the buttons do nothing".
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const ref = useCallback((next: HTMLDivElement | null) => {
    el.current = next;
    setNode(next);
  }, []);
  const [containerWidth, setContainerWidth] = useState(0);
  const [spanS, setSpanS] = useState<number>(DEFAULT_SPAN_S);

  useEffect(() => {
    if (!node) return;
    const update = () => setContainerWidth(node.clientWidth);
    update();
    // Cards resize with the window and with the inspector's layout changes.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(node);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [node]);

  const chartWidth = chartWidthFor(durationS, spanS, containerWidth);
  const scrolls = chartWidth > containerWidth + 1;

  /** Bring a moment into view — used when a click elsewhere pins a time. */
  const scrollToTime = useCallback(
    (t: number) => {
      const target = el.current;
      if (!target || !scrolls || durationS <= 0) return;
      const x = (t / durationS) * chartWidth;
      target.scrollTo({
        left: Math.max(0, x - target.clientWidth / 2),
        behavior: 'smooth',
      });
    },
    [chartWidth, durationS, scrolls],
  );

  /**
   * Keep a moving moment in view (Play all) without re-centring on every
   * tick: nothing moves while it is inside the visible window, and once it
   * reaches the last 10 % the trace pages forward so it sits 10 % in from the
   * left again. A jump backwards (a click earlier in the trace) pages back the
   * same way.
   */
  const followTime = useCallback(
    (t: number) => {
      const target = el.current;
      if (!target || !scrolls || durationS <= 0) return;
      const x = (t / durationS) * chartWidth;
      const left = target.scrollLeft;
      const w = target.clientWidth;
      if (x >= left + w * 0.1 && x <= left + w * 0.9) return;
      target.scrollTo({ left: Math.max(0, x - w * 0.1), behavior: 'smooth' });
    },
    [chartWidth, durationS, scrolls],
  );

  return {
    ref,
    el,
    containerWidth,
    chartWidth,
    spanS,
    setSpanS,
    scrolls,
    scrollToTime,
    followTime,
  };
}

/** The zoom strip. Renders nothing when the session is too short to scroll. */
export function TraceZoom({
  spanS,
  onChange,
  durationS,
}: {
  spanS: number;
  onChange: (next: number) => void;
  durationS: number;
}) {
  const spans = TRACE_SPANS.filter((s) => spanIsUseful(s, durationS));
  // One option ('Whole') means the whole session already fits; a zoom control
  // with nothing to choose is noise.
  if (spans.length < 2) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        justifyContent: 'flex-end',
        mb: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Show
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={spanS}
        onChange={(_e, next) => {
          // Null when the active button is re-clicked; keep the current span
          // rather than dropping to an undefined viewport.
          if (next !== null) onChange(next as number);
        }}
        aria-label="Trace time span"
      >
        {spans.map((s) => (
          <ToggleButton
            key={s.seconds}
            value={s.seconds}
            sx={{ px: 1, py: 0.25, fontSize: 11, textTransform: 'none' }}
          >
            {s.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
